import React, { useCallback, useContext, useMemo } from "react";
import _ from "lodash";
import * as yup from "yup";
import HeadingSection from "../UI/HeadingSection";
import { Form, Formik } from "formik";
import { Field, FormButtonCluster } from "../UI/forms";
import * as r from "../../requests/issueReportManagementRequests";
import AlertContext from "../../context/alert/AlertContext";
import UserContext from "../../context/user/UserContext";

const validationSchema = yup.object().shape({
  status: yup.number("Status is required!"),
  priority: yup.number("Issue Priority is required!"),
  report: yup.string().nullable(),
});

const IssueReportForm = ({
  data,
  count,
  issueReportId,
  setIssueReportId,
  currentIssueReport,
  setShow,
  hasForm = true,
  setData,
}) => {
  const { showSuccessAlert } = useContext(AlertContext);
  const { activeUser } = useContext(UserContext);

  const { priority, status, report } = currentIssueReport || {};

  const initialValues = {
    priority: priority || 0,
    status: status || 0,
    report: report || "",
  };

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const { rows } = data;

        if (issueReportId === "create") {
          const result = await r.createIssueReport({ input: values });
          const newObj = {
            ...result,
            sameAsShipping: "0",
          };

          const newRows = _.sortBy([...rows, newObj], ["createdAt"]);

          const newData = { rows: newRows, count: count + 1 };
          setIssueReportId(newObj.id);

          showSuccessAlert(`Successfully created IssueReport: ${newObj.id}`);
          setData(newData);
          setShow(false);
        } else {
          const updatedRecord = await r.updateIssueReport({
            id: issueReportId,
            input: values,
          });

          showSuccessAlert(
            `Successfully updated IssueReport: ${issueReportId}`
          );

          const newRows = _.sortBy(
            rows.map((row) => {
              const { id: rowId } = row;
              if (rowId === issueReportId) {
                return updatedRecord;
              }
              return row;
            }),
            ["createdAt"]
          );

          const newData = { ...data, rows: newRows };
          setData(newData);
          resetForm();
          setShow(false);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      data,
      issueReportId,
      count,
      setIssueReportId,
      showSuccessAlert,
      setData,
      setShow,
    ]
  );

  const priorityOptions = useMemo(
    () =>
      ["Very Low", "Low", "Medium", "High", "Very High"].map(
        (priorityLabel, i) => (
          <option value={i} key={i}>
            {priorityLabel}
          </option>
        )
      ),
    []
  );
  const statusOptions = useMemo(
    () =>
      [
        "To Do",
        "In Progress",
        "Ready For QA",
        "Awaiting Deployment",
        "In Production",
      ].map((statusLabel, i) => (
        <option value={i} key={i}>
          {statusLabel}
        </option>
      )),
    []
  );

  const onDelete = useCallback(() => {
    r.deleteIssueReport({ id: issueReportId });
    const { rows } = data;
    const newRows = [...rows];
    _.remove(newRows, (row) => row.id === issueReportId);
    setData({ rows: newRows, count: count - 1 });
    setShow(false);
  }, [issueReportId, count, data, setData, setShow]);

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
    >
      {(formProps) => {
        const { values } = formProps;
        return (
          <>
            <HeadingSection
              my="1rem"
              hideCreate
              hideBack
              backButtonLink={() => setShow(false)}
              title={`${
                issueReportId === "create" ? "Create" : "Edit"
              } Issue Report`}
              subtitle="Noticing an issue with the app? Please let us know by filling out an issue report below. Please describe in detail what happened and any other information about what led up to your issue."
            />
            <Form style={{ minWidth: "300px", width: "100%" }}>
              <Field name="title" labelText="Title" />
              <Field as="textarea" name="report" labelText="Report" />
              <Field as="select" name="priority" labelText="Priority Level">
                {priorityOptions}
              </Field>
              {activeUser?.roleLevel === 9 && (
                <Field as="select" name="status" labelText="Issue Status">
                  {statusOptions}
                </Field>
              )}
              <FormButtonCluster
                onDelete={onDelete}
                onCancel={() => setShow(false)}
                hideDelete={issueReportId === "create"}
                recordAction={issueReportId === "create" ? "create" : "edit"}
                deleteMessage={`Are you sure you want to delete ${values.name}?`}
              />
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

export default IssueReportForm;
